/**
 * MobileSessionManagement.tsx
 *
 * Mobile view for managing production sessions.
 * Lists active/planned sessions with progress bars.
 * Actions: Pause, Resume, Complete sessions.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLanguage } from '../../../i18n/LanguageContext';
import productionService from '../../../services/productionService';
import operatorService from '../../../services/operatorService';
import toast from 'react-hot-toast';
import {
  LayoutGrid,
  Loader2,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Package,
  User,
  MapPin,
  Clock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { getStatusBadgeClass } from '../../../utils/statusColors';

interface Session {
  session_number: string;
  sub_session_number?: string;
  product_sku: string;
  product_name: string;
  status: string;
  station_id?: number;
  station_name?: string;
  operator_name?: string;
  packer_name?: string;
  initial_material_weight?: number;
  target_output_weight?: number;
  actual_output_weight?: number;
  total_boxes?: number;
  boxes_produced?: number;
  started_at?: string;
  scheduled_date?: string;
  sub_sessions?: Array<{ sub_session_number: string; status: string }>;
}

// Get the first sub_session_number from a session (parent sessions have nested sub_sessions)
const getSubSessionId = (session: Session): string | undefined =>
  session.sub_session_number || session.sub_sessions?.[0]?.sub_session_number;

const MobileSessionManagement: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('active');

  const stationFilter = searchParams.get('station');

  // Fetch sessions
  const { data: sessions, isLoading } = useQuery(
    ['mobile-sessions'],
    () => productionService.getProductionSessions({ include: 'sub_sessions' }),
    { staleTime: 30000 }
  );

  // Filter sessions
  const filteredSessions = React.useMemo(() => {
    if (!sessions) return [];
    let filtered = sessions;

    // Filter by status
    if (statusFilter === 'active') {
      filtered = filtered.filter((s: Session) =>
        ['active', 'submitted', 'received', 'ready'].includes(s.status)
      );
    } else if (statusFilter !== 'all') {
      filtered = filtered.filter((s: Session) => s.status === statusFilter);
    }

    // Filter by station if provided
    if (stationFilter) {
      filtered = filtered.filter((s: Session) =>
        s.station_id?.toString() === stationFilter
      );
    }

    return filtered;
  }, [sessions, statusFilter, stationFilter]);

  // Pause session mutation — routes through operator-service with sub_session_number
  const pauseMutation = useMutation(
    (subSessionNumber: string) => operatorService.pauseSession(subSessionNumber, 'supervisor_pause', true),
    {
      onSuccess: () => {
        toast.success(t('supervisor.sessionPaused') || 'Session paused');
        queryClient.invalidateQueries(['mobile-sessions']);
      },
      onError: () => {
        toast.error(t('common.error') || 'Failed to pause session');
      },
    }
  );

  // Resume session mutation — routes through operator-service with sub_session_number
  const resumeMutation = useMutation(
    (subSessionNumber: string) => operatorService.resumeSession(subSessionNumber, undefined, true),
    {
      onSuccess: () => {
        toast.success(t('supervisor.sessionResumed') || 'Session resumed');
        queryClient.invalidateQueries(['mobile-sessions']);
      },
      onError: () => {
        toast.error(t('common.error') || 'Failed to resume session');
      },
    }
  );

  const getSessionStatusBadge = (status: string) => {
    const labelMap: Record<string, string> = {
      active: t('status.active') || 'Active',
      in_progress: t('status.active') || 'Active',
      planned: t('status.planned') || 'Submitted',
      ready: t('status.ready') || 'Ready',
      paused: t('status.paused') || 'Paused',
      completed: t('status.completed') || 'Completed',
      cancelled: t('status.cancelled') || 'Cancelled',
    };
    return { text: labelMap[status] || status, className: getStatusBadgeClass(status) };
  };

  const getProgress = (session: Session) => {
    if (session.total_boxes && session.total_boxes > 0) {
      return {
        current: session.boxes_produced || 0,
        total: session.total_boxes,
        percentage: Math.min(100, ((session.boxes_produced || 0) / session.total_boxes) * 100),
        unit: 'boxes',
      };
    }
    if (session.target_output_weight && session.target_output_weight > 0) {
      return {
        current: session.actual_output_weight || 0,
        total: session.target_output_weight,
        percentage: Math.min(100, ((session.actual_output_weight || 0) / session.target_output_weight) * 100),
        unit: 'lbs',
      };
    }
    return null;
  };

  const handlePause = (session: Session, e: React.MouseEvent) => {
    e.stopPropagation();
    const subSessionNumber = getSubSessionId(session);
    if (!subSessionNumber) { toast.error('No sub-session found'); return; }
    pauseMutation.mutate(subSessionNumber);
  };

  const handleResume = (session: Session, e: React.MouseEvent) => {
    e.stopPropagation();
    const subSessionNumber = getSubSessionId(session);
    if (!subSessionNumber) { toast.error('No sub-session found'); return; }
    resumeMutation.mutate(subSessionNumber);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
        <button
          onClick={() => navigate('/supervisor')}
          className="p-2 -ml-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors"
        >
          <LayoutGrid className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold">{t('mobileSupervisor.sessionManagement') || 'Sessions'}</h1>
          <p className="text-sm opacity-80">
            {filteredSessions.length} {t('supervisor.sessions') || 'sessions'}
          </p>
        </div>
        {isLoading && <Loader2 className="w-5 h-5 animate-spin opacity-70" />}
      </header>

      {/* Status Filter */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
        <div className="flex gap-2 overflow-x-auto">
          {[
            { value: 'active', label: t('supervisor.activeAndPlanned') || 'Active' },
            { value: 'paused', label: t('supervisor.paused') || 'Paused' },
            { value: 'completed', label: t('supervisor.completed') || 'Completed' },
            { value: 'all', label: t('common.all') || 'All' },
          ].map((filter) => (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                statusFilter === filter.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Session List */}
      <main className="flex-1 p-4 space-y-3">
        {isLoading && !sessions ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {t('supervisor.noSessionsFound') || 'No sessions found'}
          </div>
        ) : (
          filteredSessions.map((session: Session) => {
            const sessionKey = session.sub_session_number || session.session_number.toString();
            const isExpanded = expandedSession === sessionKey;
            const statusBadge = getSessionStatusBadge(session.status);
            const progress = getProgress(session);
            const isActive = session.status === 'active' || session.status === 'in_progress';
            const isPaused = session.status === 'paused';

            return (
              <div
                key={sessionKey}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                {/* Main Card Content */}
                <button
                  onClick={() => setExpandedSession(isExpanded ? null : sessionKey)}
                  className="w-full p-4 text-left"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {session.product_name || session.product_sku}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {session.sub_session_number || session.session_number}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${statusBadge.className}`}>
                      {statusBadge.text}
                    </span>
                  </div>

                  {/* Station & Operator */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {session.station_name && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{session.station_name}</span>
                      </div>
                    )}
                    {session.operator_name && (
                      <div className="flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        <span>{session.operator_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  {progress && (
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-500">{t('supervisor.progress') || 'Progress'}</span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {Number(progress.current).toFixed(progress.unit === 'lbs' ? 1 : 0)}/{Number(progress.total).toFixed(progress.unit === 'lbs' ? 1 : 0)} {progress.unit}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            progress.percentage >= 100 ? 'bg-green-500' :
                            progress.percentage >= 75 ? 'bg-blue-500' :
                            'bg-purple-500'
                          }`}
                          style={{ width: `${progress.percentage}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Expand Indicator */}
                  <div className="flex justify-center mt-2">
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Details & Actions */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700 pt-3">
                    {/* Details Grid */}
                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('supervisor.initialWeight')}</span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {session.initial_material_weight ? `${Number(session.initial_material_weight).toFixed(2)} lbs` : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('supervisor.targetOutput')}</span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {session.target_output_weight ? `${Number(session.target_output_weight).toFixed(2)} lbs` : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('supervisor.actualOutput')}</span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {session.actual_output_weight ? `${Number(session.actual_output_weight).toFixed(2)} lbs` : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('supervisor.startedAt')}</span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {session.started_at ? new Date(session.started_at).toLocaleTimeString() : '-'}
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {isActive && (
                        <button
                          onClick={(e) => handlePause(session, e)}
                          disabled={pauseMutation.isLoading}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-yellow-500 text-white rounded-lg font-medium active:scale-95 transition-transform disabled:opacity-50"
                        >
                          {pauseMutation.isLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Pause className="w-4 h-4" />
                          )}
                          {t('supervisor.pause') || 'Pause'}
                        </button>
                      )}
                      {isPaused && (
                        <button
                          onClick={(e) => handleResume(session, e)}
                          disabled={resumeMutation.isLoading}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-500 text-white rounded-lg font-medium active:scale-95 transition-transform disabled:opacity-50"
                        >
                          {resumeMutation.isLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                          {t('supervisor.resume') || 'Resume'}
                        </button>
                      )}
                      {(isActive || isPaused) && (
                        <button
                          onClick={() => navigate(`/supervisor/sessions/${session.session_number}`)}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg font-medium active:scale-95 transition-transform"
                        >
                          <CheckCircle className="w-4 h-4" />
                          {t('supervisor.viewDetails') || 'Details'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </main>
    </div>
  );
};

export default MobileSessionManagement;
